import { SliceZone } from '@prismicio/react';
import { Ribbon } from 'api';
import { isNil } from 'lodash-es';
import { motion } from 'motion/react';
import { parseCookies } from 'nookies';
import { useMemo } from 'react';

import { useTopicPagePlaylistsQuery } from 'api';
import { SailthruDataContentType } from 'lib/sailthru';

import { FILTER_BAR_CONSTANTS } from 'components/pages/talks/TalkIndex/FilterBar/Dropdowns/constants';
import { components } from 'components/PRISMIC/slices';
import { TopicPageDocument } from 'prismic-types';
import { TopicContentProvider, useTopicContent } from './TopicContentContext';

// Import the classic topic page components
import { Playlist, TopicListItem, Video } from 'api';
import cx from 'classnames';
import { LazyLoadAd, TopicPageAds } from 'components/ads';
import ContentContainer from 'components/content-container';
import Divider from 'components/divider';
import Grid from 'components/grid';
import Col from 'components/grid/col';
import PageHeader from 'components/page-header';
import NumberedCardRibbon from 'components/pages/homepage/NumberedCardRibbon';
import Skeleton from 'components/pages/homepage/StandardRibbon/Skeleton';
import TopicArticles from 'components/pages/topic/slug/articles';
import TopicPlaylists from 'components/pages/topic/slug/playlists';
import TopicSidebar from 'components/pages/topic/slug/sidebar';
import TopicTalks from 'components/pages/topic/slug/talks';
import StandardRibbon from 'components/StandardRibbon';
import { TopicConfigData } from 'lib/topics/config';
import { useIntl } from 'react-intl';

// Animation variants
const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
};

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

const staggerContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

export type Article = {
  title: string;
  link: string;
  thumbnail?: string;
  description: string;
  datePosted: string;
};

type TopicPageNewProps = {
  // Configuration
  useNewLayout?: boolean;
  topicConfig?: TopicConfigData;
  topicPageData?: TopicPageDocument;

  // Data for both layouts
  slug: string;
  name: string;
  programmerRibbons?: Array<Ribbon>;
  sailthruDataContent?: SailthruDataContentType;

  // Classic layout data
  articles?: Article[];
  articlesTag?: string;
  articlesTotalCount?: number;
  playlists?: Playlist[];
  playlistsTotalCount?: number;
  talks?: Video[];
  talksTotalCount?: number;
  topicsList?: TopicListItem[];
  hardcodedTopicsData?: any;
};

const MAX_ARTICLES_SHOWN = 10;

const TopicPageContent = ({
  useNewLayout = false,
  topicConfig,
  topicPageData,
  slug,
  name,
  programmerRibbons = [],
  sailthruDataContent,
  talks = [],
  articles = [],
  articlesTag = '',
  articlesTotalCount = 0,
  playlists = [],
  playlistsTotalCount = 0,
  talksTotalCount = 0,
  topicsList = [],
  hardcodedTopicsData
}: TopicPageNewProps) => {
  const { formatMessage: t } = useIntl();
  const { heroContentIds } = useTopicContent();
  const sailthruCookie = parseCookies().sailthru_hid;
  const hasSailthruCookie = !isNil(sailthruCookie);

  // Use CMS configuration when available
  const shouldUseNew = useNewLayout || topicConfig?.enableNewLayout;
  const mixpanelTitle =
    topicConfig?.mixpanelContext || `${slug}-topic-launchpad`;
  const heroTitle = `Newest ${name}`;
  const playlistTitle = `${name} Playlists`;

  const topicSlug = slug.replace('+', ' ');

  // GraphQL query for playlists
  const { data: playlistData, loading: isFetchingPlaylists } =
    useTopicPagePlaylistsQuery({
      variables: {
        topics: [topicSlug],
        first: 4
      },
      skip: !shouldUseNew || !sailthruDataContent
    });

  // Filter talks to exclude hero content IDs and format as Slot objects for StandardRibbon
  const filteredNewestTalks = useMemo(() => {
    if (!talks.length) return [];

    return talks
      .filter(talk => !heroContentIds.includes(String(talk.id)))
      .slice(0, 4) // Take only 4 for StandardRibbon
      .map((talk, index) => ({
        order: index + 1,
        video: {
          ...talk
        }
      }));
  }, [talks, heroContentIds]);

  // Process playlist data for NumberedCardRibbon
  const playlistTalks = useMemo(() => {
    if (!playlistData?.playlists?.nodes) return [];

    // Sort playlists: featured first, then by displayOrder/updatedAt
    const sortedPlaylists = [...playlistData.playlists.nodes]
      .filter(Boolean)
      .sort((a, b) => {
        if (!a || !b) return 0;

        // Featured playlists first
        if (a.featuredSlotNumber && !b.featuredSlotNumber) return -1;
        if (!a.featuredSlotNumber && b.featuredSlotNumber) return 1;

        // If both featured, sort by featuredSlotNumber
        if (a.featuredSlotNumber && b.featuredSlotNumber) {
          return a.featuredSlotNumber - b.featuredSlotNumber;
        }

        // Otherwise sort by displayOrder (most recent first)
        return (b.displayOrder || 0) - (a.displayOrder || 0);
      })
      .slice(0, 6);

    // Transform playlist data to look like talk data for NumberedCard
    return sortedPlaylists.map(
      playlist =>
        ({
          ...playlist,
          isPlaylist: true
        }) as Playlist & { isPlaylist: true }
    );
  }, [playlistData]);

  if (shouldUseNew && sailthruDataContent) {
    return (
      <motion.div variants={pageVariants} initial="hidden" animate="visible">
        <motion.div variants={sectionVariants}>
          <LazyLoadAd showLoader={false}>
            <TopicPageAds.TopicsAdBillBoardATF custParams={{ topic: slug }} />
          </LazyLoadAd>
        </motion.div>

        {/* Hero Section */}
        {topicPageData?.data.slices && (
          <motion.div
            className="bg-white pt-8 lg-tui:pt-10"
            variants={sectionVariants}
          >
            <SliceZone
              slices={topicPageData.data.slices}
              components={components}
            />
          </motion.div>
        )}

        {/* Discovery Tabs Section */}
        {topicPageData?.data?.slices1 && (
          <motion.section
            id={`launchpad-topic-${slug}`}
            className="bg-pureBlack"
            variants={sectionVariants}
          >
            <SliceZone
              slices={topicPageData?.data?.slices1}
              components={components}
            />
          </motion.section>
        )}

        {/* Newest Talks Ribbon - Using GraphQL data filtered to exclude hero content */}
        <motion.section
          id={`${slug}_standard_ribbon`}
          data-testid={`section__${slug}_standard_ribbon`}
          className="bg-white md-tui:pt-8"
          variants={sectionVariants}
        >
          <StandardRibbon
            hasLink
            isProgrammer
            hasSailthruCookie={false}
            data={filteredNewestTalks}
            sailthruSectionId=""
            title={heroTitle}
            linkText="See all Newest"
            mixpanelTitle={`${slug}-newest-ribbon`}
            linkUrl={`/talks?sort=${FILTER_BAR_CONSTANTS.DEFAULT_SORT}&topics%5B0%5D=${slug}`}
            hideTopic
            className="!border-t-[0] md-tui:!border-t-thin"
            wrapperClassName="pb-16 lg-tui:pb-20"
          />
        </motion.section>

        {/* Podcasts Section */}
        {topicPageData?.data.slices5 && (
          <motion.div variants={sectionVariants}>
            <SliceZone
              slices={topicPageData.data.slices5}
              components={components}
            />
          </motion.div>
        )}

        {/* Events Section */}
        {topicPageData?.data.slices2 && (
          <motion.div variants={sectionVariants}>
            <SliceZone
              slices={topicPageData.data.slices2}
              components={components}
            />
          </motion.div>
        )}

        <motion.section
          data-testid={`section__${slug}-playlists`}
          className="bg-white md-tui:pt-8"
          variants={sectionVariants}
        >
          {isFetchingPlaylists ? (
            <Skeleton
              ribbonClasses="border-t-[0] md-tui:border-t-thin"
              isMobileWidth={false}
              ribbonCardsClasses="grid-cols-2 gap-x-10 gap-y-4"
            />
          ) : (
            <NumberedCardRibbon
              talks={playlistTalks}
              title={playlistTitle}
              mixpanelTitle={`${slug}-playlists`}
              link={{
                visible: true,
                text: 'See all Playlists',
                url: `/playlists?topics[]=${slug}&page=1`
              }}
              wrapperClassName="pb-16 lg-tui:pb-20"
              className="!border-t-[0] md-tui:!border-t-thin"
              orderBy="row"
            />
          )}
        </motion.section>

        {/* Newsletter Section */}
        {topicPageData?.data.slices3 && (
          <motion.div variants={sectionVariants}>
            <SliceZone
              slices={topicPageData.data.slices3}
              components={components}
            />
          </motion.div>
        )}
      </motion.div>
    );
  }

  // Classic layout with animations
  const hasMoreArticles = articlesTotalCount > MAX_ARTICLES_SHOWN;
  const blurb =
    hardcodedTopicsData?.topics?.[slug]?.blurb ||
    hardcodedTopicsData?.topics?.default?.blurb ||
    `Explore TED Talks on ${name}`;
  const sidebarData = hardcodedTopicsData?.topics?.[slug]?.sidebar;
  const description = blurb.replace('%{topic_name}', name);

  const subtitleLink = description.includes('TED Member events')
    ? 'https://www.ted.com/membership'
    : null;
  const subTitleLinkText = description.includes('TED Member events')
    ? 'Join TED today to attend live.'
    : null;

  let skipPlaylistsLink: string | null = null;
  if (talks.length > 0) {
    skipPlaylistsLink = '#talks';
  } else if (articles.length > 0) {
    skipPlaylistsLink = '#articles';
  }

  const skipTalksLink = articles.length > 0 ? '#articles' : null;
  const hasContentBelowPlaylists = talks.length > 0 || articles.length > 0;
  const hasContentBelowTalks = articles.length > 0;

  const dividerBelowPlaylistsClasses = cx('my-10 md:my-12', {
    'mt-2 md:mt-4': playlistsTotalCount <= playlists.length
  });

  const dividerBelowTalksClasses = cx('my-10 md:my-12', {
    'mt-2 md:mt-4': talksTotalCount <= talks.length
  });

  const topicRedirect = topicsList?.find(
    topic => topic.slug === slug.toLowerCase()
  )?.name;

  return (
    <motion.div
      className="w-full bg-white"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={sectionVariants}>
        <LazyLoadAd showLoader={false}>
          <TopicPageAds.TopicsAdBillBoardATF />
        </LazyLoadAd>
      </motion.div>

      <motion.div variants={sectionVariants}>
        <PageHeader
          leftAligned
          breadcrumbsLinks={[
            {
              label: t({
                defaultMessage: 'Discover',
                id: 'topic.breadcrumbs.discover'
              })
            },
            {
              label: t({
                defaultMessage: 'Topics',
                id: 'topic.breadcrumbs.topics'
              }),
              link: '/topics'
            }
          ]}
          title={name}
          subtitle={description}
          subtitleLink={subtitleLink}
          subTitleLinkText={subTitleLinkText}
        />
      </motion.div>

      <motion.div variants={sectionVariants}>
        <LazyLoadAd>
          <TopicPageAds.TopicResultsAdInlineATFMobile />
        </LazyLoadAd>
      </motion.div>

      <motion.div variants={staggerContainerVariants}>
        <ContentContainer className="mb-10 md:mb-12 lg:mb-20">
          <Grid className="mt-10 md:mt-12 xl:mt-20">
            <Col sm={4} lg={8}>
              {playlists.length > 0 && (
                <motion.div variants={sectionVariants}>
                  <TopicPlaylists
                    name={name}
                    playlists={playlists}
                    totalCount={playlistsTotalCount}
                    skipLink={skipPlaylistsLink || ''}
                    slug={slug}
                  />
                  {hasContentBelowPlaylists && (
                    <Divider className={dividerBelowPlaylistsClasses} />
                  )}
                </motion.div>
              )}
              {talks.length > 0 && (
                <motion.div variants={sectionVariants}>
                  <TopicTalks
                    name={name}
                    slug={encodeURIComponent(topicRedirect || '')}
                    talks={talks}
                    totalCount={talksTotalCount}
                    skipLink={skipTalksLink || ''}
                  />
                  {hasContentBelowTalks && (
                    <Divider className={dividerBelowTalksClasses} />
                  )}
                </motion.div>
              )}
              {articles.length > 0 && (
                <motion.div variants={sectionVariants}>
                  <TopicArticles
                    articles={articles}
                    name={name}
                    moreLink={
                      hasMoreArticles
                        ? `https://ideas.ted.com/tag/${articlesTag}`
                        : undefined
                    }
                  />
                </motion.div>
              )}
            </Col>

            <Col sm={4} lg={4}>
              <motion.div variants={sectionVariants}>
                <Divider className="my-10 md:my-12 lg:hidden" />
                <Grid>
                  <Col sm={4} md={6} lg={12} className="lg:flex lg:justify-end">
                    <LazyLoadAd>
                      <TopicPageAds.TopicResultsAdInlineATFDesktop />
                    </LazyLoadAd>
                  </Col>
                  {sidebarData && (
                    <Col
                      sm={4}
                      md={6}
                      lg={12}
                      className="lg:flex lg:justify-end"
                    >
                      <TopicSidebar data={sidebarData} />
                    </Col>
                  )}
                </Grid>
              </motion.div>
            </Col>
          </Grid>
        </ContentContainer>
      </motion.div>
    </motion.div>
  );
};

const TopicPage = (props: TopicPageNewProps) => {
  return (
    <TopicContentProvider>
      <TopicPageContent {...props} />
    </TopicContentProvider>
  );
};

export default TopicPage;
